import React from 'react';
import {Formik, Form, Field, ErrorMessage} from 'formik';
import * as Yup from 'yup';
import {controlClass, fieldError, primaryButtonClass, Spinner} from '../formStyles';

export interface ResetPasswordValues {
    password: string;
    confirmPassword: string;
}

const passwordSchema = Yup.object({
  // Matches the Supabase project's minimum; there are no complexity rules to mirror.
  password: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('New password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords do not match')
    .required('Please confirm your new password'),
});

interface ResetPasswordFormProps {
    submitting: boolean;
    /**
     * Overridden by the first-password screen after signup, where "new" is wrong — that account has
     * never had one. Same two fields and the same rules, so it is the same form.
     */
    passwordLabel?: string;
    confirmLabel?: string;
    submitLabel?: string;
    submittingLabel?: string;
    onSubmit: (values: ResetPasswordValues) => Promise<void>;
}

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
  submitting,
  passwordLabel = 'New password',
  confirmLabel = 'Confirm new password',
  submitLabel = 'Set new password',
  submittingLabel = 'Updating password…',
  onSubmit,
}) => (
  <Formik
    initialValues={{password: '', confirmPassword: ''}}
    validationSchema={passwordSchema}
    onSubmit={async (values, {setSubmitting}) => {
      await onSubmit(values);
      setSubmitting(false);
    }}
    validateOnChange={false}
    validateOnBlur={false}
  >
    {({isSubmitting}) => {
      const busy = isSubmitting || submitting;

      return (
        <Form className="mt-5 space-y-3.5">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-300" htmlFor="password">
              {passwordLabel} <span className="font-normal text-gray-600">(min. 6 characters)</span>
            </label>
            <Field
              id="password"
              type="password"
              name="password"
              autoComplete="new-password"
              autoFocus
              className={controlClass}
              placeholder="••••••••"
            />
            <ErrorMessage name="password">{fieldError}</ErrorMessage>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-300" htmlFor="confirmPassword">
              {confirmLabel}
            </label>
            <Field
              id="confirmPassword"
              type="password"
              name="confirmPassword"
              autoComplete="new-password"
              className={controlClass}
              placeholder="••••••••"
            />
            <ErrorMessage name="confirmPassword">{fieldError}</ErrorMessage>
          </div>

          <button type="submit" disabled={busy} className={`w-full ${primaryButtonClass}`}>
            {busy && <Spinner />}
            {busy ? submittingLabel : submitLabel}
          </button>
        </Form>
      );
    }}
  </Formik>
);

export default ResetPasswordForm;
